import { getUserPreferredFacilities } from "@services/payment";
import { getCurrentUserId } from "@utils/userUtils";
import {
  leftoverFacilityNpi,
  leftoverHasAdminPromo,
  leftoverPreferredPk,
  mergeAdminPromoIntoLeftoverFacility,
  preferredFacilityRowsFromResponse,
} from "@utils/leftoverUnpaidCheckout";

function numericUserId(): number | undefined {
  const n = Number(getCurrentUserId());
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function leftoverPromoBound(leftover: any, merged: any): boolean {
  if (!leftoverHasAdminPromo(merged) && leftoverPreferredPk(merged) == null) {
    return false;
  }
  const npi = leftoverFacilityNpi(leftover);
  const mergedNpi = leftoverFacilityNpi(merged);
  if (npi.length === 10) return mergedNpi === npi;
  return true;
}

async function safePreferredRows(
  page: number,
  pageSize: number,
  userId?: number,
  isActive?: boolean,
): Promise<any[]> {
  try {
    const res =
      typeof isActive === "boolean"
        ? await getUserPreferredFacilities(page, pageSize, userId, isActive)
        : await getUserPreferredFacilities(page, pageSize, userId);
    return preferredFacilityRowsFromResponse(res);
  } catch {
    return [];
  }
}

function dedupePreferredRows(rows: any[]): any[] {
  const seen = new Set<string>();
  const out: any[] = [];
  for (const row of rows) {
    const key = String(row?.id ?? leftoverFacilityNpi(row) ?? out.length);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
}

/**
 * AB#10916 follow-up: leftover Mackay promo lives on the admin /
 * user-preferred row (FREE_30_*), not on getMyFacilities. Fetch that row
 * (do not keep only is_active=true — leftover unpaid is often inactive)
 * and merge promo + preferred PK onto leftover NPI 1760402663.
 *
 * Live 5313 still missed Mackay when user-scoped list returned sibling
 * Arco only (API defaults is_active). Retry inactive + unfiltered.
 */
export async function fetchAndMergeAdminPromoForLeftover(
  leftover: any,
): Promise<any> {
  if (!leftover) return leftover;
  const userId = numericUserId();
  try {
    let rows = await safePreferredRows(1, 50, userId);
    let merged = mergeAdminPromoIntoLeftoverFacility(leftover, rows);
    if (leftoverPromoBound(leftover, merged)) return merged;

    const inactiveRows = await safePreferredRows(1, 50, userId, false);
    rows = dedupePreferredRows([...rows, ...inactiveRows]);
    merged = mergeAdminPromoIntoLeftoverFacility(leftover, rows);
    if (leftoverPromoBound(leftover, merged)) return merged;

    const allRows = await safePreferredRows(1, 50);
    rows = dedupePreferredRows([...rows, ...allRows]);
    return mergeAdminPromoIntoLeftoverFacility(leftover, rows);
  } catch {
    return leftover;
  }
}
